import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ForecastRow } from '@/hooks/useRevenueIntelligence';
import { formatUiDate, formatUiNumber } from '@/lib/ui-format';

interface ForecastChartProps {
  rows: ForecastRow[];
}

interface ForecastTooltipPayloadItem {
  value?: number;
  dataKey?: string;
}

interface ForecastTooltipProps {
  active?: boolean;
  payload?: ForecastTooltipPayloadItem[];
  label?: string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: ForecastTooltipProps): JSX.Element | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const meetings = payload.find((item) => item.dataKey === 'meetings')?.value;

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-lg">
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <p className="text-sm font-bold text-status-info">
        {typeof meetings === 'number'
          ? formatUiNumber(meetings, { maximumFractionDigits: 0 })
          : meetings}{' '}
        meetings
      </p>
    </div>
  );
};

export const ForecastChart = ({ rows }: ForecastChartProps): JSX.Element => {
  if (rows.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">Pipeline Forecast</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No forecast data available
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = rows.map((row) => ({
    date: formatUiDate(row.forecastDate, {
      month: 'short',
      day: 'numeric',
      year: undefined,
    }),
    meetings: row.predictedMeetings,
    revenue: row.predictedRevenue,
  }));

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Pipeline Forecast</CardTitle>
        <p className="text-sm text-muted-foreground">Predicted meetings and pipeline value</p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="forecastMeetings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-status-info)" stopOpacity={0.4} />
                <stop offset="95%" stopColor="var(--color-status-info)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" className="text-xs" tick={{ fill: 'var(--muted-foreground)' }} />
            <YAxis className="text-xs" tick={{ fill: 'var(--muted-foreground)' }} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="meetings"
              stroke="var(--color-status-info)"
              fill="url(#forecastMeetings)"
              strokeWidth={2}
              dot={{ r: 2, fill: 'var(--color-status-info)' }}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
